import React, { useCallback, useContext, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { useStore } from 'core/store';
import { PolicyTabs } from 'core/store/account-store/policies-store/policy-store/policy-store';
import { get, getUmbrellaSuffix, policyTypes } from 'core/helpers/lookups';
import { awsDateToStringDate } from 'core/helpers/formatters';
import { showExpirationInsteadOfEndDate, endDateToExpirationDisplay } from 'core/helpers/expiration-date';
import { PolicyStatus, PolicyStatusLabel, isImportedPolicy } from 'core/helpers/policy-status';
import { showsLastInspectionField } from 'core/helpers/policy-type';
import { resolveLastInspectionDate } from 'core/helpers/last-inspection';
import { Label } from 'core/components/label';
import LabelWithCopyButton from 'core/components/label-with-copy-button';
import { IconButtonWithTooltip } from 'core/components/icon-button-with-tooltip';
import { AuthContext } from 'core/components/auth';
import classNames from 'classnames';
import { Tag } from 'core/components/tag';
import { Tooltip } from 'core/components/tooltip';
import backIcon from 'core/components/screen-header/go-to.svg';
import navigateIcon from 'core/assets/svg/ic-navigate.svg';
import agencyIcon from 'customer/assets/icSold.svg';
import FrontContact from 'customer/components/front-contact/front-contact';

import useStyles from './policy-header.styles';

const getPolicyType = get(policyTypes);

const PolicyHeader = ({ history }) => {
  const store = useStore();
  const { account: accountStore } = store;
  const policy = accountStore?.policies?.policy?.policy;
  const { policyId, id } = useParams();
  const accountId = accountStore.id || id;
  const { isExternalSales, isAgency } = useContext(AuthContext);
  const policyStatus = policy ? (accountStore?.policies?.policy?.status ?? null) : null;
  const classes = useStyles();

  const isFarm = isFarmBasedPolicyType(policy?.policyType);

  // #33158: most recent inspection date (stored Policy.lastInspectionDate vs the live completed
  // inspection for this term). Internal-only; shown on non-auto policies when a date exists.
  const lastInspectionDisplay = resolveLastInspectionDate({
    storedDate: policy?.lastInspectionDate,
    inspectionDateCompleted: accountStore?.policies?.policy?.inspection?.dateCompleted
  });
  const containerRef = useRef(null);
  const buttonContainerRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current && buttonContainerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        buttonContainerRef.current.style.transform = `translateX(${rect.left - 64}px)`;
      }
    };

    let timeoutId = null;
    const debouncedUpdatePosition = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(updatePosition, 30);
    };

    updatePosition(); // Initial call
    window.addEventListener('resize', debouncedUpdatePosition);

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', debouncedUpdatePosition);
    };
  }, []);

  const titleText = () => {
    if (!(policy?.id && policy?.id === policyId)) {
      return accountStore.accountLoading ? 'Loading...' : `${accountStore.fname} ${accountStore.lname}/Loading...`;
    }
    const umbrellaSuffix = getUmbrellaSuffix(accountStore?.policies?.policy);
    return `${accountStore.fname} ${accountStore.lname}/${getPolicyType(policy.policyType)}${umbrellaSuffix} Policy`;
  };

  const title = titleText();

  // Term dates mirror the Settings "Policy Term" panel: effectiveDate (start) and
  // endDate (last full day of coverage, stored directly with no date math).
  // awsDateToStringDate parses the AWS "yyyy-MM-dd" string via parseISO (local time),
  // avoiding the UTC-midnight off-by-one that `new Date(awsString)` causes in US timezones.
  // For brands with showExpirationDateInsteadOfEndDate, display the expiration date (endDate + 1)
  // instead — except on a flat cancellation (endDate === effectiveDate, never in force). (#32070)
  const isFlatCancel = policy?.endDate && policy?.endDate === policy?.effectiveDate;
  const displayEndDate =
    showExpirationInsteadOfEndDate() && !isFlatCancel ? endDateToExpirationDisplay(policy?.endDate) : policy?.endDate;
  const termDates =
    policy?.effectiveDate && policy?.endDate
      ? `${awsDateToStringDate(policy.effectiveDate)} - ${awsDateToStringDate(displayEndDate)}`
      : null;

  const onBack = useCallback(() => {
    history.push(`/customer/${accountId}`);
  }, [accountId, history]);

  return (
    <Grid container className={classes.header} justifyContent="space-between">
      <Grid item className={classes.container} ref={containerRef}>
        <div className={classes.fixedButtonContainer} ref={buttonContainerRef}>
          <IconButtonWithTooltip
            tooltip="Go back to previous page"
            icon={backIcon}
            onClick={onBack}
            iconClassName={classes.icon}
            imgClassName={classes.iconImg}
            placement="right"
          />
          {isFarm && (
            <IconButtonWithTooltip
              tooltip="Navigate this policy"
              icon={navigateIcon}
              onClick={() => {
                // set the current tab to DETAILS
                accountStore.policies.policy.setCurrentTab(PolicyTabs.DETAILS);
                accountStore.policies.policy.setFarmNavigatorOpen(true);
              }}
              iconClassName={classes.icon}
              imgClassName={classes.navigatorIconImg}
              placement="right"
            />
          )}
        </div>
        <Label type="title">{title}</Label>
        {!isExternalSales && <FrontContact />}
      </Grid>
      <Grid item className={classes.rightContainer}>
        <>
          {policy?.isAgentSold && !isExternalSales && (
            <div className={classes.rightSideInfo}>
              <Tag className={classes.agencyTag}>
                <img alt="agency sold icon" src={agencyIcon} />
                Agency policy
              </Tag>
            </div>
          )}
          <div className={classes.rightSideInfo}>
            <Label type="infoLabel" className={classes.rightLabel}>
              PolicyID
            </Label>
            <LabelWithCopyButton type="body3Bold">{policyId}</LabelWithCopyButton>
          </div>
          {(isFarm || isImportedPolicy(policy)) && (
            <>
              <div className={classes.rightSideInfo}>
                <Label type="infoLabel" className={classes.sourceLabel}>
                  Source
                </Label>
                <Tooltip
                  className={classes.sourceTooltipIcon}
                  text="Source indicates whether this policy was imported from a previous system or created directly in Staff."
                  placement="bottom"
                  smallIcon={{}}
                />
                <Label type="infoValue" className={classes.rightValue}>
                  {policy?.source === 'imt-conversion'
                    ? 'IMT import'
                    : policy?.source === 'guidewire-conversion'
                      ? 'Guidewire import'
                      : 'Created in Staff'}
                </Label>
              </div>
              {policy?.importedData?.conversionMetadata?.policyNumber && (
                <div className={classes.rightSideInfo}>
                  <Label type="infoLabel" className={classes.rightLabel}>
                    Prior Policy ID
                  </Label>
                  <Label type="infoValue" className={classes.rightValue}>
                    {policy.importedData.conversionMetadata.policyNumber}
                  </Label>
                </div>
              )}
            </>
          )}
          {!isAgency && showsLastInspectionField(policy?.policyType) && lastInspectionDisplay && (
            <div className={classes.rightSideInfo}>
              <Label type="infoLabel" className={classes.rightLabel}>
                Last Inspection
              </Label>
              <Label type="infoValue" className={classes.rightValue}>
                {lastInspectionDisplay}
              </Label>
            </div>
          )}
          {termDates && (
            <div className={classes.rightSideInfo}>
              <Label type="infoLabel" className={classes.rightLabel}>
                Term Dates
              </Label>
              <Label type="infoValue" className={classes.rightValue}>
                {termDates}
              </Label>
              {policyStatus && (
                <div
                  className={classNames(classes.statusPillBase, {
                    [classes.statusPillActive]: policyStatus === PolicyStatus.Active,
                    [classes.statusPillFuture]: policyStatus === PolicyStatus.Future,
                    [classes.statusPillInCancellation]: policyStatus === PolicyStatus.InCancellation,
                    [classes.statusPillCancelled]: policyStatus === PolicyStatus.Cancelled,
                    [classes.statusPillInactive]: policyStatus === PolicyStatus.Inactive
                  })}
                >
                  {PolicyStatusLabel[policyStatus] ?? policyStatus}
                </div>
              )}
            </div>
          )}
        </>
      </Grid>
    </Grid>
  );
};

export default observer(PolicyHeader);
